import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

// Progress-driven first-run. It does NOT front-load a pitch; the account already
// exists. It reacts to how many people are actually in the Top 8:
//   0      -> a non-blocking banner telling them to add their first person
//             (the editor's add bar pulses); a stray page click can't dismiss it
//   1..7   -> the banner counts up so they know to keep going
//   8 full -> the finale (share + put it on your home screen), the one real modal,
//             which only closes via Done, never an outside click.
// Finishing (or an explicit Skip) records onboardedAt so it never shows again.
const FLAG_KEY = 'yt8_onboarded';

export default function Onboarding({ top8Count = 0, uid, username, displayName, preview = false, onClose }) {
  const [closed, setClosed] = useState(false);
  const [shared, setShared] = useState(false);
  const [widgetMsg, setWidgetMsg] = useState('');

  // In-app only: the bridge is injected solely by the Android WebView, so on the
  // web this is undefined and the button never renders. Preview forces it so the
  // demo shows the full finale.
  const canAddWidget = typeof window !== 'undefined'
    && window.YT8Native && typeof window.YT8Native.promptAddWidget === 'function';
  const showWidgetBtn = preview || canAddWidget;

  const profileUrl = `${window.location.origin}/${username}`;

  const finish = async () => {
    setClosed(true);
    if (onClose) onClose();
    if (preview) return; // demo never persists anything
    try { localStorage.setItem(FLAG_KEY, '1'); } catch {}
    if (uid) { try { await updateDoc(doc(db, 'users', uid), { onboardedAt: new Date() }); } catch {} }
  };

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: `${displayName}'s Top 8`, url: profileUrl });
      else await navigator.clipboard.writeText(profileUrl);
      setShared(true);
    } catch { /* user dismissed the share sheet */ }
  };

  const addWidget = () => {
    try {
      const ok = window.YT8Native.promptAddWidget();
      setWidgetMsg(ok ? 'Check your home screen.' : 'Long-press your home screen, then Widgets, to add it.');
    } catch {
      setWidgetMsg('Long-press your home screen, then Widgets, to add it.');
    }
  };

  if (closed) return null;

  // Still filling: an in-flow coaching banner, not a blocking overlay.
  if (top8Count < 8) {
    const line = top8Count === 0
      ? 'Pick the first person to add to Your Top 8.'
      : `${top8Count} of 8 added. Keep going.`;
    return (
      <div className="onboard-banner">
        <span className="onboard-banner-dot" aria-hidden="true" />
        <span style={{ flex: 1 }}>{line}</span>
        <button className="onboard-banner-skip" onClick={finish}>Skip</button>
      </div>
    );
  }

  // Top 8 is full: the finale. No outside-click dismiss; only Done closes it.
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800 }}>That's your Top 8.</h2>
        <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>
          Share it so people can see where they land{showWidgetBtn ? ', and keep it on your home screen' : ''}.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
          <button className="btn btn-secondary" onClick={share}>
            {shared ? '✓ Link ready to share' : 'Share my Top 8'}
          </button>
          {showWidgetBtn && (
            <button className="btn btn-secondary" onClick={addWidget}>
              Add to my home screen
            </button>
          )}
          {widgetMsg && (
            <span style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>{widgetMsg}</span>
          )}
        </div>

        <div style={{ textAlign: 'right', marginTop: 22 }}>
          <button className="btn btn-primary" onClick={finish}>Done</button>
        </div>
      </div>
    </div>
  );
}
